import { useEffect } from 'react';
import { HomeThemed } from '../components/HomeThemed';
import { Seo, JsonLd } from '../components/Seo';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { samuelPersonSchema } from '../lib/jsonld';

export const HomePage = () => {
  const settings = useSiteSettings();

  // If the user clicked a nav anchor on a non-home page, that handler stashes
  // the target slug in sessionStorage and navigates here. Once the sections
  // are mounted, scroll to it. The literal sentinel `'top'` is special-cased
  // to mean "scroll to position 0" (the hero) — this is what the shared
  // goHome() helper sets when the brand logo is clicked from a sub-page.
  useEffect(() => {
    const pending =
      typeof window !== 'undefined' ? window.sessionStorage.getItem('bc.pendingScroll') : null;
    if (!pending) return;
    window.sessionStorage.removeItem('bc.pendingScroll');
    const tid = window.setTimeout(() => {
      if (pending === 'top') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      const el = document.querySelector(pending);
      el?.scrollIntoView({ behavior: 'smooth' });
    }, 600);
    return () => window.clearTimeout(tid);
  }, []);

  return (
    <>
      <Seo
        // Title kept 50-70 chars. AI Agency keyword pair leads; brand
        // tail. AEO crawlers index the static <title> in index.html —
        // this <Seo> handles the React-driven head for client-side
        // route changes so they stay in sync.
        title="AI Agency Mooresville NC · Charlotte · Lake Norman | BRANDCENTRAL"
        description={settings.defaultMetaDescription}
        path="/"
      />

      {/* Dynamic LocalBusiness JSON-LD driven by src/lib/siteSettings.ts.
          Static fallback in index.html stays in place for non-JS / cold
          crawlers (LLM bots, older AEO crawlers); this React-rendered block
          uses a different @id so Googlebot v8 (which renders JS) can pick
          up live admin edits without conflicting with the static schema. */}
      <JsonLd
        id="localbusiness-live"
        data={{
          '@context': 'https://schema.org',
          '@type': 'ProfessionalService',
          '@id': 'https://brandctl.com/#business-live',
          name: settings.siteName,
          alternateName: ['BRANDCENTRAL AI Agency', 'BRANDCENTRAL Digital Operations Agency'],
          slogan: 'AI Agency · Operations · Web · Apps',
          description: settings.defaultMetaDescription,
          url: 'https://brandctl.com',
          logo: settings.ogImageUrl,
          image: settings.ogImageUrl,
          email: settings.email,
          priceRange: '$$',
          serviceType: [
            'AI Agency',
            'AI Agent Development',
            'Web Design',
            'App Development',
            'Operations Consulting',
          ],
          knowsAbout: [
            'AI Agency',
            'AI agents',
            'AI agent development',
            'AI automation',
            'AI consulting',
            'Custom AI development',
            'ChatGPT consulting',
            'Claude agent development',
            'AI workflow automation',
            'Business AI integration',
            'AI for Charlotte businesses',
            'AI for Mooresville businesses',
            'AI for Lake Norman businesses',
            'Web design',
            'Custom website development',
            'Operations consulting',
            'Workflow automation',
            'Mobile app development',
            'App development',
            'Local SEO',
          ],
          address: {
            '@type': 'PostalAddress',
            addressLocality: settings.businessCity,
            addressRegion: settings.businessRegion,
            postalCode: settings.businessPostalCode,
            addressCountry: settings.businessCountry,
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: settings.latitude,
            longitude: settings.longitude,
          },
          areaServed: settings.areaServed.map((label) => ({
            '@type': 'Place',
            name: label,
          })),
        }}
      />

      {/* Speakable schema — marks the hero intro as voice-readable for AI
          assistants + Google Assistant + AEO crawlers. Targets the
          [data-speakable="true"] hero subtitle in HomeThemed. */}
      <JsonLd
        id="speakable-home"
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          '@id': 'https://brandctl.com/#webpage',
          url: 'https://brandctl.com',
          speakable: {
            '@type': 'SpeakableSpecification',
            cssSelector: ['[data-speakable="true"]'],
          },
        }}
      />

      {/* Person schema — boosts E-E-A-T by attributing the business to a
          real founder. Uses the canonical samuelPersonSchema so the @id
          matches /about, /blog, and /studio references — Google collapses
          all of them into a single Person entity for cumulative weight. */}
      <JsonLd id="founder" data={samuelPersonSchema} />

      <main className="selection:bg-black selection:text-white" style={{ background: '#fdfdfc' }}>
        <HomeThemed />
      </main>
      {/* The assistant chat widget is mounted once at the app root (App.tsx)
          so it appears on every route — no per-page mount here. */}
    </>
  );
};
